import { FC, ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Autocomplete, Box, Checkbox, Chip, TextField } from '@mui/material'

import { useGetTagGroupsListQuery, useGetTagListQuery } from '../../../api/core'
import { useLiveEventsTrackingUserLanguage } from '../../features/live-events-tracking/hooks/useLiveEventsTrackingUserLanguage'
import { EventTypeOption, buildEventTypeOptions, resolveEventTypeSelection } from './eventTypeOptions'

interface EventTypeSelectProps {
  /** Selected live event type ids */
  value: string[]
  onChange: (eventTypeIds: string[]) => void
  label: string
  placeholder?: string
  helperText?: ReactNode
  disabled?: boolean
  /** Stops the selection growing past this many types. */
  maxSelected?: number
  /** Shown next to an event type that the caller can count, e.g. its active events */
  eventCounts?: { [eventTypeId: string]: number }
}

/**
 * Multi select for live event types, grouped by event category.
 */
export const EventTypeSelect: FC<EventTypeSelectProps> = ({ value, onChange, label, placeholder, helperText, disabled = false, maxSelected, eventCounts }) => {
  const { t } = useTranslation()
  const userLanguage = useLiveEventsTrackingUserLanguage()
  const eventTagGroupsQuery = useGetTagGroupsListQuery({ type: 'event', userLanguage })
  const eventTagsQuery = useGetTagListQuery({ type: 'event', userLanguage })
  const isLoading = eventTagGroupsQuery.isFetching || eventTagsQuery.isFetching

  const availableOptions = useMemo(() => buildEventTypeOptions(eventTagGroupsQuery.data, eventTagsQuery.data), [eventTagGroupsQuery.data, eventTagsQuery.data])

  const selectedOptions = useMemo(
    () => resolveEventTypeSelection(value, availableOptions, { unknownCategoryLabel: t('internal-live-events:blueprint_group_uncategorized') }),
    [value, availableOptions, t]
  )

  // A selected event type that is no longer in the tag list has to be an option of its own, or the
  // Autocomplete treats the selection as invalid and drops it.
  const options = useMemo(() => {
    const unknownOptions = selectedOptions.filter((selected) => !availableOptions.some((option) => option.id === selected.id))
    return unknownOptions.length > 0 ? [...availableOptions, ...unknownOptions] : availableOptions
  }, [availableOptions, selectedOptions])

  const handleChange = (_event: unknown, newValue: EventTypeOption[]) => {
    onChange(newValue.map((option) => option.id))
  }

  const isAtMaxSelection = maxSelected !== undefined && value.length >= maxSelected

  const countOf = (option: EventTypeOption) => eventCounts?.[option.id]

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      value={selectedOptions}
      onChange={handleChange}
      options={options}
      groupBy={(option) => option.categoryName}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      getOptionDisabled={(option) => isAtMaxSelection && !value.includes(option.id)}
      loading={isLoading}
      disabled={disabled || isLoading}
      fullWidth
      size="small"
      renderInput={(params) => <TextField {...params} label={label} placeholder={placeholder} helperText={helperText} />}
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option.id}>
          <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0 }} />
          {option.name}
          {countOf(option) !== undefined && (
            <Box component="span" sx={{ ml: 1, color: 'text.secondary' }}>
              {t('internal-live-events:allowed_event_types_event_count', { count: countOf(option) })}
            </Box>
          )}
        </li>
      )}
      renderTags={(tags, getTagProps) =>
        tags.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={
              countOf(option) === undefined
                ? option.name
                : `${option.name} ${t('internal-live-events:allowed_event_types_event_count', { count: countOf(option) })}`
            }
            size="small"
          />
        ))
      }
    />
  )
}
